import { createCrudApi } from './crud';

export interface AmStationOwner {
  id?: number;
  ownerName: string;
  ownerPhone?: string | null;
  ownerEmail?: string | null;
  bankAccount?: string | null;
  ifscCode?: string | null;
  bankName?: string | null;
  upiId?: string | null;
  panNumber?: string | null;
  rentSharePercent?: number;
  isPrimary?: boolean;
}

export interface AmStationCharger {
  id?: number;
  chargerIndex?: number | null;
  chargerType?: string | null;
  capacityKw?: number | null;
  make?: string | null;
  modelNumber?: string | null;
  serialNumber?: string | null;
  status?: string;
  installedDate?: string | null;
  warrantyExpiry?: string | null;
}

export interface RentAgreementInput {
  monthlyRentAmount?: number | null;
  rentStartDate?: string | null;
  rentDueDay?: number | null;
  escalationPercent?: number | null;
  securityDepositAmount?: number | null;
  paymentModePreference?: 'NEFT' | 'RTGS' | 'UPI' | 'Cheque' | null;
  rentRevisionFrequency?: 'Yearly' | '2-Yearly' | '3-Yearly' | null;
  agreementStartDate?: string | null;
  agreementEndDate?: string | null;
  agreementNumber?: string | null;
  noticePeriodDays?: number | null;
  lockInPeriodMonths?: number | null;
  autoRenewal?: boolean;
  renewalNoticeDays?: number | null;
  stampDutyAmount?: number | null;
  stampDutyDate?: string | null;
  documentUrl?: string | null;
}
export interface AmRentAgreement extends RentAgreementInput { id: number; status: string }

export interface ElectricityConnectionInput {
  discomName?: string | null;
  consumerNumber?: string | null;
  bbpsBillerId?: string | null;
  connectionType?: 'LT' | 'HT' | null;
  sanctionedLoadKw?: number | null;
  contractDemandKva?: number | null;
  tariffCategory?: 'Commercial' | 'Industrial' | 'EV-specific' | 'Mixed-use' | null;
  meterNumber?: string | null;
  meterType?: 'Smart' | 'Conventional' | 'Net-meter' | null;
  billDueDay?: number | null;
  averageMonthlyUnitsKwh?: number | null;
  // sensitive — sent only when entered/changed; never returned in clear
  discomRegisteredMobile?: string | null;
  discomRegisteredEmail?: string | null;
  discomPortalLoginId?: string | null;
}
export interface AmElectricityConnection {
  id: number;
  discomName: string | null;
  consumerNumber: string | null;
  bbpsBillerId: string | null;
  connectionType: 'LT' | 'HT' | null;
  sanctionedLoadKw: number | null;
  contractDemandKva: number | null;
  tariffCategory: string | null;
  meterNumber: string | null;
  meterType: string | null;
  billDueDay: number | null;
  averageMonthlyUnitsKwh: number | null;
  discomRegisteredMobileMasked: string | null;
  discomRegisteredEmailMasked: string | null;
  discomPortalLoginIdMasked: string | null;
  hasMobile: boolean;
  hasEmail: boolean;
  hasLogin: boolean;
}

export interface AmStation {
  id: number;
  stationCode: string;
  clientId: number | null;
  clientName: string | null;
  stationName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  landmark: string | null;
  latitude: number | null;
  longitude: number | null;
  commissioningDate: string | null;
  propertyType: string | null;
  propertyDocStatus: string | null;
  parkingType: string | null;
  siteAreaSqft: number | null;
  propertyLink: string | null;
  sitePhotos: string[];
  ownerType: 'single' | 'company' | 'multiple';
  companyName: string | null;
  companyGstin: string | null;
  companyGstCertificateUrl: string | null;
  companySignatory: string | null;
  assignedOpsManager: string | null;
  internalNotes: string | null;
  // legacy flat primary-owner mirror
  ownerName: string | null;
  ownerPhone: string | null;
  ownerBankAccount: string | null;
  ownerIfsc: string | null;
  ownerUpi: string | null;
  status: 'active' | 'inactive';
  remarks: string | null;
  createdAt: string;
  owners: AmStationOwner[];
  chargers: AmStationCharger[];
  rentAgreement: AmRentAgreement | null;
  electricityConnection: AmElectricityConnection | null;
}

export interface CreateStationInput {
  clientId?: number | null;
  stationName: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  landmark?: string;
  latitude?: number | null;
  longitude?: number | null;
  commissioningDate?: string | null;
  propertyType?: string;
  propertyDocStatus?: string;
  parkingType?: string;
  siteAreaSqft?: number | null;
  propertyLink?: string | null;
  sitePhotos?: string[];
  ownerType?: 'single' | 'company' | 'multiple';
  companyName?: string;
  companyGstin?: string;
  companyGstCertificateUrl?: string;
  companySignatory?: string;
  assignedOpsManager?: string;
  internalNotes?: string;
  status?: 'active' | 'inactive';
  remarks?: string;
  owners?: AmStationOwner[];
  chargers?: AmStationCharger[];
  rentAgreement?: RentAgreementInput;
  electricityConnection?: ElectricityConnectionInput;
}

export const amStationsApi = createCrudApi<AmStation, CreateStationInput>('am-stations');
